import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../api/axios";

const AuthContext = createContext();
const SESSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const EMPLOYEE_SESSION_KEY = "employee_session";
const LARGE_PROFILE_FIELDS = [
  "avatar",
  "offerLetter",
  "signedOfferLetter",
  "bankPassbook",
  "aadharCard",
  "idCardPhoto",
  "experienceLetter",
  "relievingLetter",
  "internshipLetter",
];

const isPublicAuthPage = () => {
  const path = window.location.pathname;
  return path === "/login" || path === "/set-password" || path === "/reset-password";
};

const readCachedEmployee = () => {
  if (!localStorage.getItem("employee_token")) return null;

  try {
    return JSON.parse(localStorage.getItem(EMPLOYEE_SESSION_KEY) || "null");
  } catch {
    localStorage.removeItem(EMPLOYEE_SESSION_KEY);
    return null;
  }
};

const getCacheSafeEmployee = (employee) => {
  if (!employee) return employee;

  const cacheSafeEmployee = { ...employee };
  LARGE_PROFILE_FIELDS.forEach((field) => {
    if (String(cacheSafeEmployee[field] || "").startsWith("data:")) {
      cacheSafeEmployee[field] = "";
    }
  });

  return cacheSafeEmployee;
};

export const AuthProvider = ({ children }) => {
  const [employee, setEmployeeState] = useState(() => readCachedEmployee());
  const [loading, setLoading] = useState(() => !employee && !isPublicAuthPage());

  const setEmployee = useCallback((nextEmployee) => {
    setEmployeeState(nextEmployee);

    if (nextEmployee) {
      try {
        localStorage.setItem(EMPLOYEE_SESSION_KEY, JSON.stringify(getCacheSafeEmployee(nextEmployee)));
      } catch (error) {
        console.warn("Employee session cache skipped:", error);
        localStorage.removeItem(EMPLOYEE_SESSION_KEY);
      }
    } else {
      localStorage.removeItem(EMPLOYEE_SESSION_KEY);
    }
  }, []);

  const fetchEmployee = useCallback(async () => {
    if (!localStorage.getItem("employee_token")) {
      setEmployee(null);
      setLoading(false);
      return null;
    }

    try {
      const res = await api.get("/api/employee/auth/me", {
        params: { _t: Date.now() },
      });
      setEmployee(res.data.employee);
      return res.data;
    } catch (error) {
      console.error("Employee session check failed:", error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [setEmployee]);

  useEffect(() => {
    fetchEmployee();
  }, [fetchEmployee]);

  useEffect(() => {
    const verifySession = () => {
      if (!localStorage.getItem("employee_token") || isPublicAuthPage()) return;
      fetchEmployee();
    };

    window.addEventListener("focus", verifySession);
    document.addEventListener("visibilitychange", verifySession);
    const intervalId = window.setInterval(verifySession, SESSION_CHECK_INTERVAL_MS);

    return () => {
      window.removeEventListener("focus", verifySession);
      document.removeEventListener("visibilitychange", verifySession);
      window.clearInterval(intervalId);
    };
  }, [fetchEmployee]);

  return (
    <AuthContext.Provider value={{ employee, setEmployee, loading, fetchEmployee }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
